import { Dialog, DialogRef } from '@angular/cdk/dialog';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  viewChild,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AppointmentStatusCode, PatientDetails, PracticeOperatory, ProgressNoteStatus, Provider, PmsType } from '@ffd/api-client';
import { AudioInputDropdownComponent, pathParameter, PushTagOnClickDirective, TagEvent } from '@libs/shared-services';
import {
  AppointmentActionType,
  AppointmentReason,
  createAppointmentActions,
  END_HOUR,
  MAXIMUM_DEFAULT_APPOINTMENT_LENGTH,
  ScheduleCalendarTime,
  selectAppointmentActionType,
  selectEditedAppointmentPreRegistrationFakePatient,
  selectIsEditedAppointmentPreRegistration,
  selectSiteSettingsPmsType,
} from '@libs/store';
import {
  AddTreatmentItem,
  CheckboxComponent,
  ConfirmDialogComponent,
  ConfirmDialogResult,
  DropdownComponent,
  DropdownItem,
  DropdownItemPrefixDirective,
  mapTreatmentToTreatmentElement,
  SelectProviderComponent,
  TooltipModule,
  VoiceTypingTextFieldComponent,
} from '@libs/ui-lib';
import { isNotNullOrUndefined, routes } from '@libs/utils';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  DeleteAppointmentModalComponent,
  DeleteAppointmentModalComponentData,
  DeleteAppointmentModalComponentResult,
} from '@libs/schedule';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DateTime } from 'luxon';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { Subscription } from 'rxjs';
import { AddNewTreatmentData } from '@libs/charting';
import { PatientSelectorComponent } from '../patient-selector';
import { SchedulesService } from '../services/schedules.service';
import { CompleteAppointmentService } from '../services/complete-appointment.service';
import { CreateAppointmentService } from './create-appointment.service';
import {
  TimePatternEditorModalComponent,
  TimePatternEditorModalComponentData,
  TimePatternEditorModalComponentResult,
} from './time-pattern-editor-modal';
import { ReasonSelectorModalComponent, ReasonSelectorModalResult } from './reason-selector-modal/reason-selector-modal.component';
import { SelectReasonComponent } from './select-reason';
import { DateTimeSelectorComponent } from './date-time-selector/date-time-selector.component';
import { ReasonSelectorModalData } from './reason-selector-modal/reason-selector-modal.service';

@Component({
  selector: 'lib-create-appointment',
  imports: [
    CommonModule,
    TranslateModule,
    SvgIconComponent,
    PatientSelectorComponent,
    DropdownComponent,
    DropdownItemPrefixDirective,
    SelectReasonComponent,
    SelectProviderComponent,
    DateTimeSelectorComponent,
    CheckboxComponent,
    ReactiveFormsModule,
    PushTagOnClickDirective,
    LottieComponent,
    VoiceTypingTextFieldComponent,
    TooltipModule,
    AudioInputDropdownComponent,
  ],
  templateUrl: './create-appointment.component.html',
  styleUrl: './create-appointment.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CreateAppointmentService],
})
export class CreateAppointmentComponent implements OnDestroy, OnInit {
  @HostListener('document:keydown.escape', ['$event']) public onKeydownHandler(_: KeyboardEvent): void {
    if (!this.isDirty()) {
      this.closeCreateAppointment();
    }
  }

  @ViewChild('voiceTypingTextField') private readonly voiceTypingTextField?: VoiceTypingTextFieldComponent;
  @ViewChild('scrollArea', { static: true }) public scrollArea: ElementRef<HTMLElement> | undefined;
  @ViewChild('reasonSelector') private readonly selectReasonComponent?: SelectReasonComponent;

  private readonly appointmentStatusDropdown = viewChild<DropdownComponent<AppointmentStatusCode>>('appointmentStatusDropdown');

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly createAppointmentService = inject(CreateAppointmentService);
  private readonly schedulesService = inject(SchedulesService);
  private readonly completeAppointmentService = inject(CompleteAppointmentService);
  private readonly store = inject(Store);

  private readonly injector = inject(Injector);
  private readonly dialog = inject(Dialog);
  private readonly destroyRef = inject(DestroyRef);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private dialogRef: DialogRef<any> | null = null;
  private transcriptionSubscription: Subscription | null = null;

  private readonly isRecording = signal(false);
  protected readonly appointmentActionType = this.store.selectSignal(selectAppointmentActionType);
  protected readonly isEditAppointment = computed(() => this.appointmentActionType() === AppointmentActionType.Edit);
  protected readonly isEditedAppointmentPreRegistration = this.store.selectSignal(selectIsEditedAppointmentPreRegistration);
  protected readonly editedAppointmentPreRegistrationFakePatient = this.store.selectSignal(
    selectEditedAppointmentPreRegistrationFakePatient,
  );
  private readonly pmsType = this.store.selectSignal(selectSiteSettingsPmsType);
  protected readonly isOpenDental = computed(() => this.pmsType() === PmsType.OPEN_DENTAL);
  protected readonly isNotesFocused = signal(false);

  protected sidebarTitle: Signal<string> = computed(() => {
    if (this.isEditAppointment()) {
      return 'home_right_side_bar_edit_appointment';
    }
    return 'home_right_side_bar_new_appointment';
  });

  protected readonly isDirty = computed(() => this.createAppointmentService.viewModel().isDirty);
  protected readonly timePatterns = computed(() => this.createAppointmentService.viewModel().timePatterns);
  protected readonly appointmentDate = computed(() => this.createAppointmentService.viewModel().appointmentDate);
  protected readonly startTime = computed(() => this.createAppointmentService.viewModel().fromTime);
  protected readonly endTime = computed(() => this.createAppointmentService.viewModel().toTime);
  protected readonly timeBlock = computed(() => this.createAppointmentService.viewModel().timeBlock);
  protected readonly appointmentLength = computed(() => this.createAppointmentService.viewModel().appointmentLength);
  protected readonly activatedRouteParams = toSignal(this.activatedRoute.paramMap);
  protected readonly patient = computed(() => this.createAppointmentService.viewModel().patient);
  protected readonly siteId = computed(() => this.activatedRouteParams()?.get(pathParameter('siteId')));
  protected readonly isShowcase = computed(() => this.siteId() === routes.showcaseSiteId);
  protected readonly saveInProgress = computed(() => this.schedulesService.viewModel().saveInProgress);
  protected readonly errorMessage = computed(() => this.schedulesService.viewModel().sidebarErrorMessage);
  protected readonly treatments = computed(() => this.createAppointmentService.viewModel().treatments.data ?? []);
  public provider = computed(() => this.createAppointmentService.viewModel().selectedProvider);
  public allOperatoryOptions = computed(() => this.createAppointmentService.viewModel().allOperatoryOptions);
  public operatory = computed(() => this.createAppointmentService.viewModel().selectedOperatory);
  public appointmentStatusOptions = computed(() => this.createAppointmentService.viewModel().appointmentStatusOptions);
  public selectedAppointmentStatus = computed(() => this.createAppointmentService.viewModel().selectedAppointmentStatus);
  public completedStatusOption = computed(() => this.createAppointmentService.viewModel().completedAppointmentStatus);
  public reasonOptions = computed(() => this.createAppointmentService.viewModel().reasonOptions);
  public selectedReasons = computed(() => {
    return this.createAppointmentService.viewModel().selectedReasons;
  });
  protected readonly selectedTreatments = computed<AddNewTreatmentData[]>(() => {
    const treatments = this.treatments();

    return this.selectedReasons()
      .map((reason) => {
        return treatments.find((t) => t.dentalCode === reason.code);
      })
      .filter((treatment) => isNotNullOrUndefined(treatment))
      .map((treatment) => {
        return { ...mapTreatmentToTreatmentElement(treatment), status: ProgressNoteStatus.C };
      });
  });
  public isReasonsDataLoading = computed(() => this.createAppointmentService.viewModel().isReasonsDataLoading);
  protected notes = computed(() => this.createAppointmentService.viewModel().notes);
  protected teeth = computed(() => this.createAppointmentService.viewModel().teeth);

  protected transcriptionFormControl = this.createAppointmentService.transcriptionFormControl;
  protected transcriptionStatus = this.createAppointmentService.transcriptionStatus;
  protected readonly transcriptionStatus$ = toObservable(this.transcriptionStatus);
  protected readonly activeDeviceId = this.createAppointmentService.activeDeviceId;
  protected lastFocusedCursorOffset = this.createAppointmentService.lastFocusedCursorOffset;
  protected isNotesInputEmpty = signal(this.notes()?.trim().length === 0);

  public timeOverlayHeight = '160px';
  public timeOverlayWidth = '270px';
  public overlayPositionStrategy: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 10,
    },
  ];

  public tentativeForm = new FormGroup({
    isTentative: new FormControl(this.createAppointmentService.viewModel().isTentative ?? false),
  });

  protected TagEvent = TagEvent;

  public readonly lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  public get appointmentLengths(): DropdownItem<number>[] {
    const minutesFromStartTimeToEndHour = END_HOUR * 60 - (this.startTime().hour * 60 + this.startTime().minute);
    const maxIntervals = Math.min(MAXIMUM_DEFAULT_APPOINTMENT_LENGTH, minutesFromStartTimeToEndHour);
    return Array.from({ length: maxIntervals / this.timeBlock() }, (_, index) => index + 1).map((value) => {
      const appointmentLength = value * this.timeBlock();
      return {
        label: `${appointmentLength} mins`,
        value: appointmentLength,
      };
    });
  }

  protected operatoryCompareFn = (o1: PracticeOperatory, o2: PracticeOperatory): boolean => o1.id === o2.id;

  public ngOnInit(): void {
    this.createAppointmentService.init();
    if (this.isEditAppointment() && this.patient()) {
      this.createAppointmentService.loadTeeth(this.patient()!, this.siteId()!);
      this.createAppointmentService.loadVisits(this.patient()!, this.siteId()!);
    }
  }

  public ngOnDestroy(): void {
    if (this.isRecording()) {
      this.toggleDictationClick();
    }
    this.createAppointmentService.clearPatientSearchTerm();
  }

  public closeCreateAppointment(): void {
    this.schedulesService.removeAppointmentSelection();
    this.schedulesService.editSelectedAppointment({ isEdited: false });
    this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
  }

  public onCloseClick(): void {
    if (this.isRecording()) {
      this.toggleDictationClick();
    }

    const hasNotesChanged = this.notes() !== this.getTextFieldNotes();

    if (this.isDirty() || hasNotesChanged) {
      this.dialogRef = this.dialog.open<ConfirmDialogResult>(ConfirmDialogComponent, {
        backdropClass: ['bg-overlay-light'],
        panelClass: ['w-[480px]'],
        disableClose: true,
        data: {
          title: 'schedule_exit_modal_title',
          description: 'schedule_exit_modal_description',
          cancelLabel: 'schedule_exit_modal_continue',
          confirmLabel: 'schedule_exit_modal_discard',
        },
      });

      this.dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
        if (result.confirm) {
          this.closeCreateAppointment();
        }
      });
    } else {
      this.closeCreateAppointment();
    }
  }

  public onOperatoryOptionChange(option?: PracticeOperatory | (PracticeOperatory | undefined)[] | null): void {
    if (option && !Array.isArray(option)) {
      this.createAppointmentService.setOperatory(option);
    }
  }

  public onAppointmentStatusOptionChange(option?: AppointmentStatusCode | (AppointmentStatusCode | undefined)[] | null): void {
    if (!option || Array.isArray(option)) {
      return;
    }

    const isCompletedStatus = option.id === this.completedStatusOption()?.id;
    const hasTreatments = this.selectedTreatments().length > 0;

    if (isCompletedStatus && hasTreatments) {
      this.handleCompleteAppointmentFlow(option);
    } else {
      this.createAppointmentService.setAppointmentStatus(option);
    }
  }

  private handleCompleteAppointmentFlow(statusOption: AppointmentStatusCode): void {
    const patient = this.patient();
    if (!patient) {
      return;
    }

    this.completeAppointmentService
      .openCompleteAppointmentModal$({
        patient,
        appointmentDate: this.appointmentDate(),
        procedures: this.selectedReasons(),
        teeth: this.teeth(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((completedTreatments) => {
        this.handleCompleteAppointmentResult(completedTreatments, statusOption, patient);
      });
  }

  private handleCompleteAppointmentResult(
    completedTreatments: AddTreatmentItem[] | undefined,
    statusOption: AppointmentStatusCode,
    patient: PatientDetails,
  ): void {
    // undefined means user cancelled or clicked skip - revert status
    if (completedTreatments === undefined) {
      const appointmentStatusDropdown = this.appointmentStatusDropdown();
      const previousAppointmentStatus = this.selectedAppointmentStatus();
      if (appointmentStatusDropdown && previousAppointmentStatus) {
        appointmentStatusDropdown.writeValue(previousAppointmentStatus);
      }
      return;
    }

    this.createAppointmentService.setAppointmentStatus(statusOption);

    // Save treatments if any were selected
    if (completedTreatments.length > 0) {
      this.completeAppointmentService.completeTreatments({
        treatments: completedTreatments,
        patientId: patient.id,
        siteId: this.siteId()!,
      });
    }
  }

  public onSaveTentativeClick(): void {
    if (this.isOpenDental() && !this.validateReasonSelection()) {
      return;
    }
    if (this.isRecording()) {
      this.toggleDictationClick();
    }
    // this setTimeout is necessary to ensure that the appointment is saved after the dictation is stopped, and we have the note ready
    setTimeout(() => {
      this.schedulesService.saveAppointment({
        type: 'create',
        isTentative: true,
        notes: this.getTextFieldNotes(),
      });
      this.store.dispatch(createAppointmentActions.onResetCreateAppointment());
    });
  }

  public onCreateClick(): void {
    if (this.isOpenDental() && !this.validateReasonSelection()) {
      return;
    }
    if (this.isRecording()) {
      this.toggleDictationClick();
    }
    // this setTimeout is necessary to ensure that the appointment is saved after the dictation is stopped,  and we have the note ready
    setTimeout(() => {
      this.schedulesService.saveAppointment({
        type: 'create',
        isTentative: false,
        notes: this.getTextFieldNotes(),
      });
    });
  }

  public onDeleteClick(): void {
    if (this.isRecording()) {
      this.toggleDictationClick();
    }
    this.dialog
      .open<DeleteAppointmentModalComponentResult, DeleteAppointmentModalComponentData>(DeleteAppointmentModalComponent, {
        backdropClass: ['bg-overlay-light'],
        panelClass: ['w-[480px]'],
        disableClose: true,
      })
      .closed.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result?.isDeleted) {
          this.schedulesService.deleteAppointment();
        }
      });
  }

  public onSaveChangesClick(): void {
    if (this.isOpenDental() && !this.validateReasonSelection()) {
      return;
    }
    if (this.isRecording()) {
      this.toggleDictationClick();
    }
    // this setTimeout is necessary to ensure that the appointment is saved after the dictation is stopped, and we have the note ready
    setTimeout(() => {
      this.schedulesService.saveAppointment({
        type: 'edit',
        isTentative: !!this.tentativeForm.controls.isTentative.value,
        notes: this.getTextFieldNotes(),
      });
    });
  }

  // eslint-disable-next-line rxjs/finnish
  public openTimePatternEditorModal(data: TimePatternEditorModalComponentData): void {
    this.dialogRef = this.dialog.open<TimePatternEditorModalComponentResult, TimePatternEditorModalComponentData>(
      TimePatternEditorModalComponent,
      {
        backdropClass: ['bg-overlay-light'],
        disableClose: true,
        data,
      },
    );

    this.dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (!result) {
        return;
      }

      this.createAppointmentService.setTimePatterns(result.timePatterns);
    });
  }

  public onDateSelect(appointmentDate: DateTime): void {
    this.createAppointmentService.setAppointmentDate(appointmentDate);
    this.schedulesService.setDate(appointmentDate.plus(0));
  }

  public onStartTimeSet(startTime: ScheduleCalendarTime): void {
    this.createAppointmentService.setStartTime(startTime);
  }

  public onEndTimeSet(endTime: ScheduleCalendarTime): void {
    this.createAppointmentService.setEndTime(endTime);
  }

  protected appointmentLengthChange(value: number | number[] | null): void {
    if (value && !Array.isArray(value)) {
      this.createAppointmentService.setAppointmentLength(value);
    }
  }

  protected onPatientSelect({
    patient,
    isInitialPatientSelect = false,
  }: {
    patient: PatientDetails;
    isInitialPatientSelect?: boolean;
  }): void {
    this.createAppointmentService.setPatient(patient, isInitialPatientSelect);
    this.createAppointmentService.loadTeeth(patient, this.siteId()!);
    this.createAppointmentService.loadVisits(patient, this.siteId()!);
  }

  public openReasonEditorModal(data: ReasonSelectorModalData): void {
    this.dialogRef = this.dialog.open<ReasonSelectorModalResult, ReasonSelectorModalData>(ReasonSelectorModalComponent, {
      backdropClass: ['bg-overlay-light'],
      disableClose: true,
      injector: this.injector,
      data,
    });

    this.dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (!result) {
        return;
      }

      this.createAppointmentService.setReasons(result.addedReasons);
    });
  }

  protected onSelectReason(reason: AppointmentReason): void {
    this.createAppointmentService.setReasons([...this.selectedReasons(), reason]);
  }

  protected onDeselectReason(reason: AppointmentReason): void {
    this.createAppointmentService.setReasons(this.selectedReasons().filter((r) => r.code !== reason.code));
  }

  public onProviderOptionChange(provider: Provider): void {
    this.createAppointmentService.setProvider(provider);
  }

  protected toggleDictationClick(): void {
    this.isRecording.set(!this.isRecording());
    this.createAppointmentService.toggleSpeechRecognitionListening(this.voiceTypingTextField?.getTemplateText() ?? '');

    if (this.transcriptionSubscription) {
      this.transcriptionSubscription.unsubscribe();
      this.transcriptionSubscription = null;
    }

    this.transcriptionSubscription = this.transcriptionFormControl()
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.scrollArea?.nativeElement.scrollTo({
          top: this.scrollArea?.nativeElement.scrollHeight,
          behavior: 'smooth',
        });
      });
  }
  protected updateLastFocusedCursorOffset(offset: number): void {
    this.createAppointmentService.updateLastFocusedCursorOffset(offset);
  }
  protected onSelectDevice(deviceId: string): void {
    this.createAppointmentService.setDeviceId(deviceId);
  }

  protected getTextFieldNotes(): string {
    return this.voiceTypingTextField?.getTemplateText() ?? '';
  }

  protected onNotesClick(): void {
    this.isNotesFocused.set(true);
  }

  protected onNotesFocusOut(ev: FocusEvent, self: HTMLElement): void {
    this.isNotesInputEmpty.set(this.voiceTypingTextField?.getTemplateText().trim().length === 0);
    if (self.contains(ev.relatedTarget as Node)) {
      return;
    }
    this.isNotesFocused.set(false);
  }

  private validateReasonSelection(): boolean {
    if (!this.selectedReasons() || this.selectedReasons().length === 0) {
      this.selectReasonComponent?.isInvalidReason.set(true);
      return false;
    }

    return true;
  }
}
